import { ReactNode, useMemo } from "react";
import { FormElementProps } from "@/common/contracts/form.contracts";
import { cn } from "@/common/utils/css.utils";

export type FormGroupProps<TElementProps extends FormElementProps> = {
  id?: string;
  label?: ReactNode | ((props: Omit<TElementProps, "id">) => ReactNode);
  element: ReactNode | ((props: Omit<TElementProps, "htmlFor">) => ReactNode);
  helper?: ReactNode | ((props: Omit<TElementProps, "id">) => ReactNode);
  valid?: boolean;
  disabled?: boolean;
  className?: string;
};

/**
 * Groups label, form element and helper component while having an option
 * to pass `valid` and `disabled` props to those components.
 */
export const FormGroup = <TElementProps extends FormElementProps>({
  id,
  label,
  element,
  helper,
  valid,
  disabled,
  className,
  ...props
}: FormGroupProps<TElementProps>) => {
  const childProps = useMemo<TElementProps>(
    () => ({ valid, disabled }) as TElementProps,
    [valid, disabled]
  );

  return (
    <div {...props} className={cn("flex flex-col space-y-1", className)}>
      {typeof label === "function"
        ? label({ ...childProps, htmlFor: id })
        : label}
      {typeof element === "function" ? element({ ...childProps, id }) : element}
      {typeof helper === "function"
        ? helper({ ...childProps, htmlFor: id })
        : helper}
    </div>
  );
};
